import React, { FC, useEffect, useState } from 'react';

import {
  Flex,
  Modal,
  Button,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react';

import { Form, Formik } from 'formik';

import FormikInput from 'components/primitives/FormikInput';
import FormikSelectInput from 'components/primitives/FormikSelectInput';
import FormikDatepicker from 'components/primitives/FormikDatepicker/FormikDatepicker.view';

import { getInitialValues, validationSchema } from './AddImpactReport.utils';

import { AddImpactReportViewProps } from './AddImpactReport.props';
import { SiteFilterableSelect } from '../SiteFilterableSelect';
import { Report } from 'types/report';
import { calculateLost } from 'utils/calculateLost';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
  noOfLines: 1,
};

const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const monthsMap: Record<number, string> = {
  1: 'Jan',
  2: 'Feb',
  3: 'Mar',
  4: 'Apr',
  5: 'May',
  6: 'Jun',
  7: 'Jul',
  8: 'Aug',
  9: 'Sep',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec',
};

const years = [2021, 2022, 2023, 2024];

const AddImpactReportView: FC<AddImpactReportViewProps> = (props) => {
  const {
    isOpen,
    onClose,
    onSubmit,
    isLoading,
    inventoryReports,
    isLoadingReports,
    isLoadingCollection,
    collectionPointsData,
    setSiteName,
    reportId,
    isValueFromHarvest,
  } = props;
  const [selectedReport, setselectedReport] = useState<
    Report | null | undefined
  >(null);

  useEffect(() => {
    if (!reportId) {
      setselectedReport(null);
    } else {
      let reportFromInventoryReport = inventoryReports.find(
        (r) => r._id === reportId,
      );
      setselectedReport(reportFromInventoryReport);
    }
  }, [inventoryReports, reportId]);

  const clearFormFields = (setFieldValue: {
    (field: string, value: any, shouldValidate?: boolean | undefined): void;
    (arg0: string, arg1: undefined): void;
  }) => {
    setFieldValue('id', undefined);
    setFieldValue('startDate', undefined);
    setFieldValue('endDate', undefined);
    setFieldValue('name', undefined);
    setFieldValue('single_use_items', undefined);
    setFieldValue('month', undefined);
    setFieldValue('year', undefined);
  };
  return (
    <Modal
      size="6xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent p="0px 10px" borderRadius="15px">
        <ModalCloseButton _focus={{ outline: 'none' }} top="5" />
        <ModalHeader textAlign="center" fontWeight="600" color="#262626">
          Create new Impact Report
        </ModalHeader>
        <ModalBody>
          {selectedReport || isValueFromHarvest === false ? (
            <Flex direction="column">
              <Formik
                onSubmit={onSubmit}
                initialValues={getInitialValues(selectedReport)}
                validationSchema={validationSchema}
                enableReinitialize={true}>
                {({ isValid, setFieldValue }) => {
                  return (
                    <Form>
                      <Flex gap="20px" flexWrap="wrap">
                        {isLoadingCollection ?? collectionPointsData}
                        <SiteFilterableSelect
                          containerProps={{ flex: 1, minW: '250px' }}
                          label="Customer Site Name"
                          name="customerSiteName"
                          labelProps={{ fontSize: '12px' }}
                          isReadOnly={selectedReport ? true : false}
                          isRequired={false}
                          onChange={(_report) => {
                            const report = collectionPointsData?.find(
                              (r) => r._id === _report,
                            );

                            setSiteName(_report);
                            clearFormFields(setFieldValue);
                          }}
                        />
                        <FormikSelectInput
                          isLoading={isLoadingReports}
                          placeholder="Select"
                          isReadOnly={selectedReport ? true : false}
                          label="Select Harvest Summary (*)"
                          containerProps={{ flex: 1, minW: '250px' }}
                          name="id"
                          onSuccess={(_report) => {
                            const report = inventoryReports.find(
                              (r) => r._id === _report,
                            );

                            const lostItems = report?.details?.reduce(
                              (acc, cur) => acc + calculateLost(cur),
                              0,
                            );

                            if (report) {
                              setFieldValue('startDate', report?.startDate);
                              setFieldValue('endDate', report?.endDate);
                              setFieldValue('name', report?.name);
                              setFieldValue('month', report?.month);
                              setFieldValue('year', report?.year);
                              setFieldValue(
                                'single_use_items',
                                report?.details?.reduce(
                                  (acc, item) => acc + item.ret,
                                  0,
                                ) + (lostItems || 0),
                              );
                            } else {
                              clearFormFields(setFieldValue);
                            }
                          }}>
                          {inventoryReports?.map((s) => {
                            return (
                              <option key={s._id} value={s._id}>
                                {s?.name || s?.site?.name || ''}
                              </option>
                            );
                          })}
                        </FormikSelectInput>
                      </Flex>

                      <Flex mt="10px" gap="20px" flexWrap="wrap">
                        <FormikInput
                          isReadOnly
                          name="name"
                          type="text"
                          label="Impact Report Name (*)"
                          labelProps={labelProps}
                          containerProps={{ flex: 1, minW: '250px' }}
                        />

                        <FormikDatepicker
                          readOnly
                          name="startDate"
                          label="Start Date (*)"
                          labelProps={labelProps}
                          containerProps={{ flex: 1, minW: '250px' }}
                        />
                        <FormikDatepicker
                          readOnly
                          name="endDate"
                          label="End Date (*)"
                          labelProps={labelProps}
                          containerProps={{ flex: 1, minW: '250px' }}
                        />
                      </Flex>

                      <Flex mt="10px" gap="20px" flexWrap="wrap">
                        <FormikSelectInput
                          isDisabled={true}
                          placeholder="Select"
                          label="Select Month (*)"
                          containerProps={{ flex: 1, minW: '250px' }}
                          name="month">
                          {months?.map((m) => (
                            <option key={m} value={m}>
                              {monthsMap[m] || ''}
                            </option>
                          ))}
                        </FormikSelectInput>

                        <FormikSelectInput
                          placeholder="Select"
                          label="Select Year (*)"
                          isDisabled={true}
                          containerProps={{ flex: 1, minW: '250px' }}
                          name="year">
                          {years.map((year, index) => (
                            <option key={year - index} value={year}>
                              {year}
                            </option>
                          ))}
                        </FormikSelectInput>
                      </Flex>

                      <Flex mt="10px" gap="20px" flexWrap="wrap">
                        <FormikInput
                          isReadOnly
                          name="single_use_items"
                          label="Single-Use Items Eliminated"
                          labelProps={labelProps}
                          containerProps={{ flex: 1, minW: '250px' }}
                        />

                        <FormikInput
                          type="string"
                          name="wd"
                          label="Pounds of Waste Diverted (*)"
                          labelProps={labelProps}
                          containerProps={{ flex: 1, minW: '250px' }}
                        />

                        <FormikInput
                          type="string"
                          name="co2m"
                          label="Pounds of CO₂ Mitigated (*)"
                          labelProps={labelProps}
                          containerProps={{ flex: 1, minW: '250px' }}
                        />
                      </Flex>

                      <Flex mt="10px" gap="20px" flexWrap="wrap">
                        <FormikInput
                          type="string"
                          name="wc"
                          label="Gallons of Water Conserved (*)"
                          labelProps={labelProps}
                          containerProps={{ flex: 1, minW: '250px' }}
                        />

                        <FormikInput
                          type="string"
                          name="er"
                          label="KWh of Energy Reduced (*)"
                          labelProps={labelProps}
                          containerProps={{ flex: 1, minW: '250px' }}
                        />

                        <FormikInput
                          type="string"
                          name="cos"
                          label="Liters of Crude Oil Saved (*)"
                          labelProps={labelProps}
                          containerProps={{ flex: 1, minW: '250px' }}
                        />
                      </Flex>

                      <Flex my={8} flexDirection="row" justifyContent="center">
                        <Button
                          variant="outline"
                          marginRight="10px"
                          w="134px"
                          onClick={onClose}
                          isDisabled={isLoading}>
                          Cancel
                        </Button>
                        <Button
                          isDisabled={!isValid}
                          w="134px"
                          type="submit"
                          isLoading={isLoading}>
                          Create
                        </Button>
                      </Flex>
                    </Form>
                  );
                }}
              </Formik>
            </Flex>
          ) : null}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddImpactReportView;
